import { PlaceholderOption } from "@/components/rich-editor/rich-editor.types.ts";
import {
  ApiEmployeeColumn,
  EmployeeColumnEntity,
  getEmployeeColumnEntity,
  parseCustomTableKey,
} from "@/types/employees/employee-column.types.ts";
import {
  WorkflowPlaceholderGroupTitles,
  WorkflowPlaceholderSubject,
} from "@/types/workflows/workflow-placeholder.types.ts";

const ENTITY_ORDER = [
  EmployeeColumnEntity.personal,
  EmployeeColumnEntity.position,
  EmployeeColumnEntity.employment,
  EmployeeColumnEntity.termination,
  EmployeeColumnEntity.custom_field,
  EmployeeColumnEntity.custom_table,
] as const;

const UNSUPPORTED_COLUMN_KEYS = new Set(["employee.position.is_manager"]);

export function buildWorkflowPlaceholderOptions(
  columns: readonly Pick<ApiEmployeeColumn, "id" | "title">[],
  subjects: readonly WorkflowPlaceholderSubject[],
  baseOptions: readonly PlaceholderOption[],
  groupTitles: WorkflowPlaceholderGroupTitles,
  tableTitleById: ReadonlyMap<number, string> = new Map()
): PlaceholderOption[] {
  const { subjectTitles, entityTitles } = groupTitles;

  const options = subjects.flatMap((subject) => {
    const prefix = `{{${subject}.`;
    const existing = baseOptions.filter((option) => option.id.startsWith(prefix));
    const seenIds = new Set(existing.map((option) => option.id));
    const fields = ENTITY_ORDER.flatMap((entity) =>
      columns.flatMap((column): PlaceholderOption[] => {
        if (
          !column.id.startsWith("employee.") ||
          getEmployeeColumnEntity(column.id) !== entity ||
          UNSUPPORTED_COLUMN_KEYS.has(column.id)
        )
          return [];

        const id = `{{${subject}.${column.id.slice("employee.".length)}}}`;
        if (seenIds.has(id)) return [];
        seenIds.add(id);
        const tableId = parseCustomTableKey(column.id)?.tableId;
        const entityTitle =
          (tableId !== undefined ? tableTitleById.get(tableId) : undefined) ?? entityTitles[entity];
        const group = subjectTitles[subject];
        const subgroup = entity === EmployeeColumnEntity.personal ? undefined : entityTitle;
        return [{ id, title: column.title, group, ...(subgroup ? { subgroup } : {}) }];
      })
    );
    return [...existing, ...fields];
  });

  const otherOptions = subjects.includes("employee")
    ? baseOptions.filter(
        (option) =>
          !["employee", "assignee", "manager"].some((subject) =>
            option.id.startsWith(`{{${subject}.`)
          )
      )
    : [];
  return [...options, ...otherOptions];
}
